import { PlaybackClock, observePageScrolling } from '@theme/carousel-playback';
import { NativeCharacterTrack } from '@theme/product-character-native';
const tapDiagnosticsEnabled = new URLSearchParams(location.search).get('carousel_debug') === '1';
/** Three real cards on stage, circular selection without scroll-end promises or clones. */
class ProductCharacterCarousel extends HTMLElement {
  #controller;
  #observer;
  #resize;
  #slides = [];
  #index = 0;
  #motion;
  #frame = 0;
  #clock = new PlaybackClock();
  #pageScrolling = false;
  #gesture;
  #queue = 0;
  #visible = false;
  #autoplayActivated = false;
  #pressedSide = null;
  #pressedCenter = null;
  #selection = null;
  #reduced = matchMedia('(prefers-reduced-motion: reduce)');
  #mobile = matchMedia('(max-width: 749px)');
  #compact = matchMedia('(max-width: 1199px)');
  #suppressUntil = 0;
  #latestPointerId = null;
  #tapTimer = 0;
  #measureImages = true;
  #collectionPaused = false;
  #coordinated = false;
  #automaticSteps = 0;
  #passReported = false;
  #native;

  connectedCallback() {
    if (this.#controller) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#slides = [...this.querySelectorAll(':scope > .character-carousel__stage > .character-carousel__slide')];
    if (!this.#slides.length) return;
    this.#index %= this.#slides.length;
    const collection = this.closest('collection-cover-carousel[data-cover-ready]');
    this.#coordinated = !!collection?.hasAttribute('data-autoplay') && Number(collection.dataset.coverCount) > 1;
    this.#collectionPaused = !!collection && (!collection.hasAttribute('data-products-revealed') || collection.hasAttribute('aria-busy') || collection.hasAttribute('data-cover-navigating'));
    this.resetCollectionPass();
    this.setAttribute('data-ready', '');
    // Decide before the first gesture. Switching engines on pointerdown would
    // replace the hit geometry of the very tap we are trying to preserve.
    if (matchMedia('(pointer: coarse)').matches) {
      this.#native = new NativeCharacterTrack(this, this.#slides, this.#index, {
        onBusy: () => this.#clock.pause(),
        onCommit: (index, automatic, changed) => {
          this.#index = index;
          this.dataset.index = String(index);
          if (changed) {
            this.#clock.reset();
            if (automatic) this.#automaticSteps++;
            else {
              this.#autoplayActivated = true;
              this.#automaticSteps = 0;
              this.#passReported = false;
              this.dispatchEvent(new CustomEvent('character-carousel:pass-reset', { bubbles: true }));
            }
          }
          this.#schedule();
        },
      });
    }
    this.#rest();
    observePageScrolling(scrolling => {
      this.#pageScrolling = scrolling;
      // Scroll pauses the next dwell, never a transition already on screen.
      this.#schedule();
    }, signal);
    // Center side cards before their nested product link can navigate.
    this.addEventListener('click', this.#click, { capture: true, signal });
    if (!this.#native) this.addEventListener('pointerdown', this.#down, { capture: true, signal });
    this.addEventListener('mousedown', event => {
      if (!event.target.closest('button, input, select, textarea')) event.stopPropagation();
    }, { capture: true, signal });
    this.addEventListener('dragstart', event => event.preventDefault(), { signal });
    if (!this.#native) window.addEventListener('pointermove', this.#move, { passive: false, signal });
    // End ownership before nested card/gallery release handlers run.
    if (!this.#native) window.addEventListener('pointerup', this.#up, { capture: true, signal });
    if (!this.#native) window.addEventListener('pointercancel', this.#cancel, { capture: true, signal });
    this.addEventListener('lostpointercapture', event => {
      // Only an active drag owns capture on the rail. A delayed loss from the
      // previous drag must not cancel the next tap on its original target.
      if (event.target === this && this.#gesture?.moved && !this.hasPointerCapture(event.pointerId)) this.#cancel(event);
    }, { signal });
    // Before Horizon's delegated document clicks: a drag must never open a PDP.
    window.addEventListener('click', event => {
      if (this.#native) return;
      if (!this.contains(event.target) || event.detail === 0) return;
      // Chrome touch clicks can arrive after the next finger press. The older
      // drag's click must not consume/reset the new tap's captured card intent.
      // Mouse/keyboard compatibility events without a pointer ID retain the
      // existing drag guard; no replacement or synthetic click is generated.
      const stalePointer = 'pointerId' in event && event.pointerId >= 0 &&
        this.#latestPointerId !== null && event.pointerId !== this.#latestPointerId;
      if (stalePointer || this.#gesture || performance.now() < this.#suppressUntil) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.#diagnoseTap(stalePointer ? 'stale-pointer' : this.#gesture ? 'gesture-active' : 'drag-click-guard', event);
      }
    }, { capture: true, signal });
    this.addEventListener('keydown', event => {
      if (event.target.closest('input, select, textarea')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        this.#autoplayActivated = true;
        event.preventDefault(); this.#advance(event.key === 'ArrowLeft' ? -1 : 1, 0, true, false, true);
      }
    }, { signal });
    this.addEventListener('focusout', () => this.#schedule(), { signal });
    this.addEventListener('focusin', () => this.#schedule(), { signal });
    document.addEventListener('close', () => this.#schedule(), { capture: true, signal });
    document.addEventListener('toggle', event => {
      if (event.target instanceof HTMLDialogElement) this.#schedule();
    }, { capture: true, signal });
    window.addEventListener('blur', this.#recover, { signal });
    window.addEventListener('pageshow', this.#recover, { signal });
    document.addEventListener('visibilitychange', this.#recover, { signal });
    this.#reduced.addEventListener('change', this.#recover, { signal });
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      // Merely peeking beneath the cover must not start moving the products.
      // Once the whole component has been seen (or an arrow used), keep normal
      // offscreen suspension/resume without re-locking the initial start gate.
      if (entry.isIntersecting && (entry.intersectionRatio >= 0.999 || (this.#coordinated && entry.intersectionRatio >= 0.5))) this.#autoplayActivated = true;
      this.#schedule();
    }, { threshold: [0, 0.5, 1] });
    this.#observer.observe(this);
    this.#resize = new ResizeObserver(() => {
      this.#measureImages = true;
      if (!this.#motion && !this.#gesture) this.#rest();
    });
    this.#resize.observe(this);
    this.#slides.forEach(slide => {
      const image = slide.querySelector('.card-gallery');
      if (image) this.#resize.observe(image);
    });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#native?.destroy(); this.#native = undefined;
    this.#controller?.abort(); this.#controller = undefined;
    this.#observer?.disconnect(); this.#resize?.disconnect();
    this.#clock.reset(); cancelAnimationFrame(this.#frame);
    clearTimeout(this.#tapTimer);
    this.#tapTimer = 0;
    this.#measureImages = true;
    this.#motion = this.#gesture = undefined;
    this.#queue = 0;
    this.#latestPointerId = null;
    this.#pressedSide = null;
    this.#pressedCenter = this.#selection = null;
    this.removeAttribute('data-moving');
    this.removeAttribute('data-dragging');
  }

  setCollectionPlayback({ paused, coordinated }) {
    if (this.#collectionPaused === paused && this.#coordinated === coordinated) return;
    this.#collectionPaused = paused;
    this.#coordinated = coordinated;
    // Cover/headline visibility may change during a page scroll. Let the
    // current move settle naturally; the paused flag gates the next advance.
    this.#schedule();
  }

  resetCollectionPass() {
    this.#automaticSteps = 0;
    this.restartCollectionDwell();
  }

  restartCollectionDwell() {
    this.#passReported = false;
    this.#clock.reset();
    this.#schedule();
  }

  // Read-only snapshot for the explicitly enabled, device-local tap recorder.
  // No geometry reads, timers, or changes to gesture/click handling.
  getTapDiagnosticState() {
    if (this.#native) return { ...this.#native.state, pageScrolling: this.#pageScrolling, collectionPaused: this.#collectionPaused };
    const slideIndex = slide => slide ? this.#slides.indexOf(slide) : null;
    return {
      index: this.#index,
      gestureId: this.#gesture?.id ?? null,
      dragging: this.#gesture?.moved ?? false,
      pressedCenter: slideIndex(this.#pressedCenter),
      pressedSide: slideIndex(this.#pressedSide),
      latestPointerId: this.#latestPointerId,
      suppressMs: Math.max(0, Math.round(this.#suppressUntil - performance.now())),
      awaitingClick: !!this.#tapTimer,
      pageScrolling: this.#pageScrolling,
      collectionPaused: this.#collectionPaused,
      motion: this.#motion ? {
        direction: this.#motion.direction,
        progress: Math.round(this.#motion.progress * 1000) / 1000,
        commit: this.#motion.commit,
        releasedDrag: this.#motion.releasedDrag,
        paused: this.#motion.pausedAt !== null,
      } : null,
    };
  }

  #diagnoseTap(reason, event) {
    // This separate passive observation survives stopImmediatePropagation on
    // the original event, even if the recorder module loaded after this rail.
    if (!tapDiagnosticsEnabled) return;
    this.dispatchEvent(new CustomEvent('character-carousel:tap-diagnostic', {
      bubbles: true, detail: { reason, event, state: this.getTapDiagnosticState() },
    }));
  }

  #mod(value) { return (value + this.#slides.length) % this.#slides.length; }
  // Mobile center is 52% wide, neighbors are 66% of that. Their half-widths
  // sum to 43.16% of the stage, so 44% spacing prevents their card backgrounds
  // overlapping even during interpolation; only the viewport clips neighbors.
  #step() { return this.clientWidth * (this.#mobile.matches ? 0.44 : this.#compact.matches ? 0.37 : 0.32); }
  #slot(index, center) {
    if (index === center) return 0;
    if (index === this.#mod(center + 1)) return 1;
    if (index === this.#mod(center - 1)) return -1;
    return 2;
  }
  #paint(slide, slot, opacity = 1) {
    const scale = 1 - Math.min(1, Math.abs(slot)) * (this.#compact.matches ? 0.34 : 0.26);
    slide.style.transform = `translate3d(${slot * this.#step()}px,0,0) scale(${scale})`;
    slide.style.opacity = String(opacity);
  }
  #paintDetails(slide, opacity) {
    slide.style.setProperty('--character-details-opacity', String(opacity));
    slide.toggleAttribute('data-details-visible', opacity > 0);
  }
  #rest() {
    if (this.#native) { this.#native.resize(); return; }
    if (this.#measureImages) {
      this.#measureImages = false;
      // Align and scale around the photo, not the variable-length product copy.
      // Geometry is read only on resize/image-layout changes, never per frame.
      this.#slides.forEach(slide => {
        const image = slide.querySelector('.card-gallery');
        if (!image || !slide.offsetWidth) return;
        const bounds = slide.getBoundingClientRect();
        const photo = image.getBoundingClientRect();
        const scale = bounds.width / slide.offsetWidth;
        if (scale > 0) slide.style.setProperty('--character-image-center', `${(photo.top - bounds.top + photo.height / 2) / scale}px`);
      });
    }
    this.removeAttribute('data-moving');
    this.dataset.index = String(this.#index);
    this.#slides.forEach((slide, index) => {
      const slot = this.#slot(index, this.#index), visible = Math.abs(slot) <= 1;
      slide.toggleAttribute('data-visible', visible);
      slide.toggleAttribute('data-center', slot === 0);
      this.#paintDetails(slide, slot === 0 ? 1 : 0);
      slide.inert = !visible;
      slide.setAttribute('aria-hidden', String(!visible));
      this.#paint(slide, slot);
      if (visible) slide.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
    });
  }
  #paintMove(direction, progress) {
    const next = this.#mod(this.#index + direction);
    this.#slides.forEach((slide, index) => {
      // Use the same progress as the photos: dragging backwards also reverses
      // the fade, without independent timers that can get out of sync.
      const detailsOpacity = index === this.#index ? Math.max(0, 1 - progress / 0.25)
        : index === next ? Math.max(0, (progress - 0.7) / 0.3) : 0;
      this.#paintDetails(slide, Math.min(1, detailsOpacity));
      let from = this.#slot(index, this.#index), to = this.#slot(index, next);
      const before = Math.abs(from) <= 1, after = Math.abs(to) <= 1;
      slide.toggleAttribute('data-visible', before || after);
      // Newly entering cards must be clickable before the final resting frame.
      slide.inert = !(before || after);
      slide.setAttribute('aria-hidden', String(!(before || after)));
      if (!before && !after) return;
      if (!before) from = direction * 2;
      if (!after) to = -direction * 2;
      let slot = from + (to - from) * progress;
      let opacity = before && after ? 1 : before ? 1 - progress : progress;
      // With only 2–3 products, the same neighbor exits then re-enters. Hide
      // its repositioning outside the stage rather than crossing the hero card.
      if (Math.abs(to - from) > 1) {
        const firstHalf = progress < 0.5;
        slot = firstHalf ? from - direction * progress * 2 : to + direction * (1 - progress) * 2;
        opacity = Math.abs(progress * 2 - 1);
      }
      this.#paint(slide, slot, opacity);
      slide.style.zIndex = String(Math.abs(slot) < 0.5 ? 2 : 1);
      if (after) slide.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
    });
  }
  #advance(direction, from = 0, commit = true, automatic = false, restartInterval = false, releasedDrag = false) {
    if (this.#slides.length < 2) return;
    if (this.#native) {
      this.#clock.reset();
      this.#native.advance(direction, automatic);
      return;
    }
    this.#clock.reset();
    if (this.#motion) {
      this.#motion.restartInterval ||= restartInterval;
      this.#queue = Math.max(-8, Math.min(8, this.#queue + direction));
      return;
    }
    const motion = { direction, start: performance.now(), from, progress: from, commit, automatic, restartInterval, releasedDrag, pausedAt: null, tick: null };
    this.#motion = motion;
    this.setAttribute('data-moving', '');
    const duration = this.#reduced.matches ? 0 : Math.max(120, 420 * (commit ? 1 - from : from));
    const tick = now => {
      if (this.#motion !== motion || motion.pausedAt !== null) return;
      const time = duration ? Math.min(1, (now - motion.start) / duration) : 1;
      const eased = 1 - (1 - time) ** 3;
      motion.progress = from + ((commit ? 1 : 0) - from) * eased;
      this.#paintMove(direction, motion.progress);
      if (time < 1) this.#frame = requestAnimationFrame(tick);
      else this.#finish(true);
    };
    motion.tick = tick;
    this.#frame = requestAnimationFrame(tick);
  }
  #resumeMotion() {
    const motion = this.#motion;
    if (!motion || motion.pausedAt === null) return;
    if (this.#gesture || this.#tapTimer || document.hidden) return;
    motion.start += performance.now() - motion.pausedAt;
    motion.pausedAt = null;
    this.#frame = requestAnimationFrame(motion.tick);
  }
  #finish(completed = false) {
    cancelAnimationFrame(this.#frame);
    const fullInterval = this.#motion?.automatic || this.#motion?.restartInterval;
    const restartPass = this.#motion?.commit && !(completed && this.#motion.automatic);
    if (this.#motion?.commit) this.#index = this.#mod(this.#index + this.#motion.direction);
    // Only a naturally completed automatic advance earns pass progress.
    // Manual selections and interrupted motion start a fresh automatic pass
    // from the chosen product; browsing cannot prematurely switch collections.
    if (this.#motion?.commit) {
      if (completed && this.#motion.automatic) this.#automaticSteps++;
      else this.#automaticSteps = 0;
    }
    this.#motion = undefined;
    this.#slides.forEach(slide => slide.style.removeProperty('z-index'));
    this.#rest();
    if (restartPass) {
      this.#passReported = false;
      this.dispatchEvent(new CustomEvent('character-carousel:pass-reset', { bubbles: true }));
    }
    if (this.#selection !== null && this.#selection !== this.#index) {
      const forward = this.#mod(this.#selection - this.#index);
      this.#advance(forward <= this.#slides.length / 2 ? 1 : -1, 0, true, false, true);
    } else if (this.#queue) {
      this.#selection = null;
      const direction = Math.sign(this.#queue); this.#queue -= direction;
      this.#advance(direction, 0, true, false, fullInterval);
    } else {
      this.#selection = null;
      this.#schedule(this.#coordinated || fullInterval ? undefined : 1500);
    }
  }
  #click = event => {
    if (this.#native) {
      if (!event.defaultPrevented && event.target.closest('[data-character-previous], [data-character-next]')) {
        this.#autoplayActivated = true;
        this.#advance(event.target.closest('[data-character-previous]') ? -1 : 1, 0, true, false, true);
      }
      return;
    }
    clearTimeout(this.#tapTimer);
    this.#tapTimer = 0;
    requestAnimationFrame(() => {
      if (!this.isConnected || this.querySelector('[data-product-navigation-pending], [data-view-transition-triggered]')) return;
      this.#schedule();
    });
    const pressedSide = this.#pressedSide;
    const pressedCenter = this.#pressedCenter;
    this.#pressedSide = null;
    this.#pressedCenter = null;
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    // A fast reversal can land in the space vacated by a moving side card.
    // Use the side intent captured at press, without synthesizing a link click.
    const slide = event.target.closest('.character-carousel__slide') ||
      (event.target === this || event.target.closest('.character-carousel__stage') ? pressedSide : null);
    const index = this.#slides.indexOf(slide);
    if (index >= 0 && !slide.inert && slide !== pressedCenter && (index !== this.#index || slide === pressedSide) &&
        !event.target.closest('button, input, select, textarea, label, [role="button"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#diagnoseTap('select-side-card', event);
      if (this.#motion) {
        const motion = this.#motion;
        const incoming = this.#mod(this.#index + motion.direction);
        if (index === this.#index || index === incoming) {
          // A fresh tap chooses the current endpoint immediately, including
          // reversing a released drag. Keep the exact painted progress instead
          // of finishing the old choice before starting another full movement.
          cancelAnimationFrame(this.#frame);
          this.#motion = undefined;
          this.#selection = null;
          this.#autoplayActivated = true;
          this.#advance(motion.direction, motion.progress, index === incoming, false, true);
        } else {
          // The far neighbor is not part of this pair; finish the pair before
          // moving to it, without snapping or changing its native click target.
          motion.restartInterval = true;
          this.#selection = index;
          this.#resumeMotion();
        }
      } else if (index !== this.#index) {
        this.#autoplayActivated = true;
        this.#advance(this.#slot(index, this.#index) === -1 ? -1 : 1, 0, true, false, true);
      } else {
        // A pointer press can finish a moving side card before its click arrives.
        this.#schedule();
      }
      return;
    }
    if (event.target.closest('[data-character-previous], [data-character-next]')) {
      this.#autoplayActivated = true;
      this.#advance(event.target.closest('[data-character-previous]') ? -1 : 1, 0, true, false, true);
    }
  };
  #down = event => {
    if (this.#native) return;
    if (!event.isPrimary || event.button !== 0) return;
    this.#latestPointerId = event.pointerId;
    // A new deliberate press is not the synthetic click from the last drag,
    // including presses on arrows/quick add which don't start another drag.
    this.#suppressUntil = 0;
    clearTimeout(this.#tapTimer);
    this.#tapTimer = 0;
    this.#pressedSide = null;
    this.#pressedCenter = null;
    if (event.button !== 0 || !event.isPrimary || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey ||
        event.target.closest('input, select, textarea, label') || this.#slides.length < 2) return;
    if (!event.target.closest('.character-carousel__stage')) return;
    const interactive = !!event.target.closest('button, [role="button"]');
    const pressed = event.target.closest('.character-carousel__slide');
    // Freeze in place, not at the destination. Snapping on pointerdown moves
    // the anchor away before pointerup, so the browser cannot produce its click.
    if (this.#motion) {
      cancelAnimationFrame(this.#frame);
      this.#motion.pausedAt ??= performance.now();
    }
    const stage = this.querySelector('.character-carousel__stage').getBoundingClientRect();
    const middle = stage.left + stage.width / 2;
    const paintedCenter = this.#slides.filter(slide => slide.hasAttribute('data-visible')).reduce((nearest, slide) => {
      const box = slide.getBoundingClientRect();
      const distance = Math.abs(box.left + box.width / 2 - middle);
      return !nearest || distance < nearest.distance ? { slide, distance } : nearest;
    }, null)?.slide;
    // A released drag already chose its destination, even before that card
    // crosses the geometric midpoint. A fresh tap on it must open the product,
    // not spend the click selecting it again. Keep its anchor frozen in place
    // through the native click; never navigate on the drag's own pointerup.
    const centered = this.#motion?.releasedDrag
      ? this.#slides[this.#mod(this.#index + (this.#motion.commit ? this.#motion.direction : 0))]
      : paintedCenter;
    if (this.#slides.includes(pressed)) {
      if (pressed === centered) this.#pressedCenter = pressed;
      else this.#pressedSide = pressed;
    } else if (this.#motion && !interactive) {
      // During motion only, retain left/right browsing targets across the gaps
      // made by cards exiting and wrapping. Center whitespace never opens a PDP.
      const side = Math.sign(event.clientX - middle);
      if (Math.abs(event.clientX - middle) >= this.#step() / 2) {
        this.#pressedSide = this.#slides[this.#mod(this.#index + side)];
      }
    }
    this.#queue = 0;
    this.#selection = null;
    this.#clock.pause();
    this.#gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false, progress: 0, direction: 1, interactive, threshold: event.pointerType === 'mouse' ? 8 : 12 };
    // Leave a tap on its original image/link, including the browser's implicit
    // touch capture. Redirecting it to the separate card-overlay link changes
    // the release/click target. Only an intentional drag takes rail capture.
    // Let the card cancel hover previews and clear its own stale drag guards.
    // Competing image dragging is already blocked by our mousedown handler;
    // swallowing pointerdown here left those card press handlers unreachable.
  };
  #move = event => {
    const gesture = this.#gesture;
    if (!gesture || event.pointerId !== gesture.id || gesture.interactive) return;
    const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
    if (!gesture.moved) {
      if (Math.abs(dy) > gesture.threshold && Math.abs(dy) > Math.abs(dx)) { this.#cancel(event); return; }
      if (Math.abs(dx) < gesture.threshold || Math.abs(dx) <= Math.abs(dy)) return;
      gesture.moved = true;
      this.#pressedSide = null;
      this.#pressedCenter = null;
      // Once this is a deliberate drag, settle the old movement and give the
      // pointer ownership. A simple press never repositions its target.
      if (this.#motion) this.#finish();
      this.setPointerCapture(event.pointerId);
      this.setAttribute('data-dragging', '');
      this.dispatchEvent(new CustomEvent('theme:drag-intent', { bubbles: true, detail: { pointerId: event.pointerId } }));
    }
    if (event.cancelable) event.preventDefault();
    gesture.direction = dx < 0 ? 1 : -1;
    gesture.progress = Math.min(1, Math.abs(dx) / Math.max(1, this.#step()));
    this.#paintMove(gesture.direction, gesture.progress);
  };
  #release() {
    const gesture = this.#gesture;
    this.#gesture = undefined;
    this.removeAttribute('data-dragging');
    if (gesture && this.hasPointerCapture(gesture.id)) this.releasePointerCapture(gesture.id);
    return gesture;
  }
  #up = event => {
    if (this.#gesture?.id !== event.pointerId) return;
    const gesture = this.#release();
    if (gesture.moved) {
      this.#suppressUntil = performance.now() + 400;
      this.#advance(gesture.direction, gesture.progress, gesture.progress > 0.15, false, false, true);
    } else {
      // Some touch browsers dispatch click after the next animation frame.
      // Hold its target until that click; a bounded fallback releases cancelled
      // taps/pointer releases outside the card without leaving autoplay stuck.
      this.#tapTimer = setTimeout(() => {
        this.#tapTimer = 0;
        this.#pressedSide = this.#pressedCenter = null;
        if (!this.isConnected || this.querySelector('[data-product-navigation-pending]')) return;
        this.#schedule();
      }, 450);
    }
  };
  #cancel = event => {
    if (this.#gesture?.id !== event.pointerId) return;
    this.#diagnoseTap(event.type === 'pointermove' ? 'vertical-pan' : event.type, event);
    this.#pressedSide = null;
    this.#pressedCenter = null;
    // A cancelled swipe/scroll cannot activate the link that held the finger.
    // The next deliberate pointerdown clears this guard immediately.
    this.#suppressUntil = performance.now() + 400;
    this.#release();
    if (this.#motion) this.#resumeMotion(); else this.#rest();
    this.#schedule();
  };
  #recover = () => {
    if (this.#native) { this.#native.recover(); this.#schedule(); return; }
    clearTimeout(this.#tapTimer);
    this.#tapTimer = 0;
    this.#pressedSide = null;
    this.#pressedCenter = this.#selection = null;
    this.#latestPointerId = null;
    this.#release(); this.#queue = 0;
    if (this.#motion) this.#finish(); else this.#rest();
    this.#schedule();
  };
  #schedule(delay) {
    this.#clock.pause();
    if (!this.isConnected || !this.#controller || this.#tapTimer || this.#native?.busy ||
        this.querySelector('[data-product-navigation-pending], [data-view-transition-triggered]')) return;
    // A vertical swipe can cancel a pointer hold in the middle of a move.
    // Resume that move even while the next automatic advance is scroll-paused.
    if (this.#motion) { this.#resumeMotion(); return; }
    if (this.#pageScrolling || this.#collectionPaused || !this.#autoplayActivated ||
        (!this.hasAttribute('data-autoplay') && !this.#coordinated) || this.#reduced.matches ||
        (!this.#coordinated && this.#slides.length < 2) || !this.#slides.length || this.#passReported) return;
    if (document.hidden || !this.#visible || this.#gesture || document.querySelector('dialog[open]') ||
        (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible'))) return;
    const interval = Math.max(2, Number(this.dataset.interval) || 4) * 1000;
    this.#clock.resume(() => {
      if (this.querySelector('[data-product-navigation-pending], [data-view-transition-triggered]')) return;
      if (this.#reduced.matches || this.#collectionPaused || this.#pageScrolling) { this.#schedule(); return; }
      if (document.hidden || !this.#visible || this.#gesture || this.#motion || this.#native?.busy || document.querySelector('dialog[open]') ||
          (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible'))) {
        this.#schedule(500); return;
      }
      if (this.#coordinated && this.#automaticSteps >= this.#slides.length - 1) {
        this.#passReported = true;
        this.dispatchEvent(new CustomEvent('character-carousel:pass-complete', { bubbles: true }));
        return;
      }
      this.#advance(1, 0, true, true);
    }, this.#coordinated && delay === 1500 ? interval : delay ?? interval);
  }
}

if (!customElements.get('product-character-carousel')) customElements.define('product-character-carousel', ProductCharacterCarousel);
